import { Routes, Route } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import { Layout } from "./components/Layout";
import { useAuthStore } from "./lib/auth";

// Lazy load all pages for code splitting
const LandingPage = lazy(() =>
  import("./pages/LandingPage").then((m) => ({ default: m.LandingPage }))
);
const ExplorePage = lazy(() =>
  import("./pages/ExplorePage").then((m) => ({ default: m.ExplorePage }))
);
const ServicePage = lazy(() =>
  import("./pages/ServicePage").then((m) => ({ default: m.ServicePage }))
);
const BotOnboardPage = lazy(() =>
  import("./pages/BotOnboardPage").then((m) => ({ default: m.BotOnboardPage }))
);
const CheckoutPage = lazy(() =>
  import("./pages/CheckoutPage").then((m) => ({ default: m.CheckoutPage }))
);
const OrderPage = lazy(() =>
  import("./pages/OrderPage").then((m) => ({ default: m.OrderPage }))
);
const OrdersPage = lazy(() =>
  import("./pages/OrdersPage").then((m) => ({ default: m.OrdersPage }))
);
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage }))
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage }))
);
const AuthPage = lazy(() =>
  import("./pages/AuthPage").then((m) => ({ default: m.AuthPage }))
);
const EditServicePage = lazy(() =>
  import("./pages/seller/EditServicePage").then((m) => ({
    default: m.EditServicePage,
  }))
);
const SupportPage = lazy(() =>
  import("./pages/SupportPage").then((m) => ({ default: m.SupportPage }))
);
const TermsPage = lazy(() =>
  import("./pages/TermsPage").then((m) => ({ default: m.TermsPage }))
);
const DocsPage = lazy(() =>
  import("./pages/DocsPage").then((m) => ({ default: m.DocsPage }))
);
const BotsPage = lazy(() =>
  import("./pages/BotsPage").then((m) => ({ default: m.BotsPage }))
);
const PromotionsNewPage = lazy(() =>
  import("./pages/PromotionsNewPage").then((m) => ({
    default: m.PromotionsNewPage,
  }))
);
const TrustPage = lazy(() =>
  import("./pages/TrustPage").then((m) => ({ default: m.TrustPage }))
);
const StatusPage = lazy(() =>
  import("./pages/StatusPage").then((m) => ({ default: m.StatusPage }))
);
const AdminModerationPage = lazy(() =>
  import("./pages/AdminModerationPage").then((m) => ({
    default: m.AdminModerationPage,
  }))
);
const PrivacyPage = lazy(() =>
  import("./pages/PrivacyPage").then((m) => ({ default: m.PrivacyPage }))
);
const ContentPolicyPage = lazy(() =>
  import("./pages/ContentPolicyPage").then((m) => ({
    default: m.ContentPolicyPage,
  }))
);

// Loading fallback component
function PageLoader() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
        paddingTop: "120px",
      }}
    >
      <div
        className="skeleton"
        style={{ width: "200px", height: "24px", borderRadius: "8px" }}
      />
    </div>
  );
}

export default function App() {
  const refreshSession = useAuthStore((state) => state.refreshSession);

  // Refresh session on app load
  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Layout />}>
          {/* Public routes */}
          <Route index element={<LandingPage />} />
          <Route path="explore" element={<ExplorePage />} />
          <Route path="services/:id" element={<ServicePage />} />
          <Route path="bots/onboard" element={<BotOnboardPage />} />

          {/* Auth routes */}
          <Route path="auth/login" element={<AuthPage mode="login" />} />
          <Route path="auth/signup" element={<AuthPage mode="signup" />} />
          <Route path="auth/callback" element={<AuthPage mode="callback" />} />

          {/* Checkout */}
          <Route path="checkout/:serviceId" element={<CheckoutPage />} />

          {/* Authenticated routes */}
          <Route path="orders" element={<OrdersPage />} />
          <Route path="orders/:id" element={<OrderPage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="bots" element={<BotsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="promotions/new" element={<PromotionsNewPage />} />
          <Route path="seller/services/new" element={<EditServicePage />} />
          <Route
            path="seller/services/:id/edit"
            element={<EditServicePage />}
          />
          <Route path="support" element={<SupportPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="content-policy" element={<ContentPolicyPage />} />
          <Route path="docs" element={<DocsPage />} />
          <Route path="trust" element={<TrustPage />} />
          <Route path="status" element={<StatusPage />} />
          <Route path="admin/moderation" element={<AdminModerationPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
